import { useEffect, useState, ReactNode } from "react";
import { ShieldCheck, Lock, Send, Paperclip, Mic, ArrowUp, ChefHat, Tag, AlertCircle, Check, FileQuestion } from "lucide-react";
import { t } from "@/i18n";

// Reveals one scripted chat item after another, then pauses and starts over.
const useScript = (timings: number[], pause = 4000) => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setStep(timings.length); return; }
    let i = 0;
    let timer: ReturnType<typeof setTimeout>;
    const next = () => {
      i = i >= timings.length ? 0 : i + 1;
      setStep(i);
      timer = setTimeout(next, i >= timings.length ? pause : timings[i]);
    };
    timer = setTimeout(next, timings[0]);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return step;
};

const Typing = () => (
  <div className="flex items-start" data-testid="mock-typing">
    <span className="bubble-in !py-1.5 !px-2.5 flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="mock-dot" style={{ animationDelay: `${i * 0.15}s` }} />)}</span>
  </div>
);

const Asker = ({ name, img, text, testid }: { name: string; img?: string; text: string; testid: string }) => (
  <div className="flex flex-col items-end mock-pop" data-testid={testid}>
    <span className="flex items-center gap-1 text-[0.55rem] faint mb-0.5">
      {name}{img ? <img src={img} alt="" className="w-3.5 h-3.5 rounded-full object-cover" /> : <span className="w-3.5 h-3.5 rounded-full bg-[#6fb3a8] text-white grid place-items-center text-[0.45rem] font-bold">{name[0]}</span>}
    </span>
    <p className="bubble-out max-w-[80%] text-[0.7rem] leading-snug">{text}</p>
  </div>
);

const Reply = ({ children, cite, testid }: { children: ReactNode; cite?: ReactNode; testid: string }) => (
  <div className="flex flex-col items-start mock-pop" data-testid={testid}>
    <div className="bubble-in max-w-[86%] text-[0.7rem] leading-snug">{children}</div>
    {cite && <span className="text-[0.55rem] faint mt-0.5 px-2 flex items-center gap-1">{cite}</span>}
  </div>
);

const Composer = () => (
  <div className="m-2 flex items-center gap-1.5 rounded-full border border-[var(--border-color)] bg-[var(--bg-surface)] px-2 py-1">
    <Paperclip size={10} className="faint" /><Mic size={10} className="faint" />
    <span className="flex-1 text-[0.6rem] faint">{t("Message")}</span>
    <span className="w-4 h-4 rounded-full bg-[var(--accent-primary)] grid place-items-center"><ArrowUp size={9} color="#fff" /></span>
  </div>
);

const ChatHead = ({ title }: { title: string }) => (
  <div className="text-center py-2 border-b border-[var(--border-color)]"><p className="text-[0.7rem] font-semibold">{title}</p></div>
);

const Row = ({ tone, title, meta, fresh }: { tone: "err" | "ok" | "info"; title: string; meta: string; fresh?: boolean }) => {
  const Icon = tone === "ok" ? Check : tone === "err" ? AlertCircle : FileQuestion;
  const color = tone === "ok" ? "var(--status-ok)" : tone === "err" ? "var(--status-error)" : "var(--accent-primary)";
  return (
    <div className={`flex items-center gap-2 py-2 px-1 border-b border-[var(--border-color)] last:border-0 rounded-md ${fresh ? "mock-pop mock-fresh" : ""}`}>
      <span className="w-5 h-5 rounded-full grid place-items-center shrink-0" style={{ background: `color-mix(in srgb, ${color} 14%, transparent)` }}><Icon size={10} color={color} /></span>
      <span className="flex-1 min-w-0"><span className="block text-[0.65rem] font-semibold truncate">{title}</span><span className="block text-[0.55rem] faint">{meta}</span></span>
    </div>
  );
};

const useSmall = () => {
  const q = "(max-width: 639px)";
  const [small, setSmall] = useState(() => !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return;
    const on = () => setSmall(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return small;
};

type Panel = "chat" | "review";
const PanelTabs = ({ panel, onPick }: { panel: Panel; onPick: (p: Panel) => void }) => (
  <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 z-10 flex gap-1 rounded-full bg-[rgba(27,27,29,0.75)] p-0.5 backdrop-blur" data-testid="desktop-flow-tabs">
    {(["chat", "review"] as const).map((k) => (
      <button key={k} type="button" onClick={() => onPick(k)} data-testid={`desktop-flow-tab-${k}`}
        className={`px-2.5 py-0.5 rounded-full text-[0.6rem] font-semibold transition-colors ${panel === k ? "bg-white text-[#1b1b1d]" : "text-white/80"}`}>
        {t(k === "chat" ? "Chat" : "Review")}
      </button>
    ))}
  </div>
);

const PLANKTON_Q = "What's the formula of the Secret Krabby Patty?";

// Desktop: Plankton asks for the secret formula; it goes to the owner and SMBOI offers the Sea Cucumber Patty instead.
export function DesktopFlow() {
  const step = useScript([1400, 1600, 1800, 3500], 5000);
  const small = useSmall();
  const [picked, setPicked] = useState<Panel | null>(null);
  useEffect(() => { if (step === 0) setPicked(null); }, [step]);
  const panel: Panel = picked ?? (step >= 4 ? "review" : "chat");
  const hide = (k: Panel) => (small && panel !== k ? "hidden" : "");
  return (
    <div className={`grid h-full relative ${small ? "grid-cols-1" : "grid-cols-2 divide-x divide-[var(--border-color)]"}`} data-testid="desktop-flow" data-panel={small ? panel : "both"}>
      {small && <PanelTabs panel={panel} onPick={setPicked} />}
      <div className={`h-full flex flex-col min-h-0 ${hide("chat")}`} data-testid="desktop-flow-chat-panel">
        <ChatHead title={`${t("Chat")} · Krusty Krab`} />
        <div className="flex-1 flex flex-col gap-2 p-3 px-4 sm:px-5 overflow-hidden">
          {step >= 1 && <Asker name="Plankton" img="/avatars/plankton.png" text={t(PLANKTON_Q)} testid="desktop-flow-question" />}
          {step === 1 && <Typing />}
          {step >= 2 && (
            <Reply testid="desktop-flow-sent" cite={<><Send size={9} className="text-[var(--accent-primary)]" />{t("Sent to owner")}</>}>
              <span className="flex items-center gap-1 font-semibold"><Lock size={10} /> {t("Not in your OI base.")}</span>
              {t("I've sent your question to Mr_Krabs.")}
            </Reply>
          )}
          {step === 2 && <Typing />}
          {step >= 3 && (
            <Reply testid="desktop-flow-recipe" cite={<><ShieldCheck size={9} className="text-[var(--status-ok)]" />{t("Sea Cucumber Patty")} · v1</>}>
              <span className="block">{t("Meanwhile, here's a recipe you can make:")}</span>
              <span className="mt-1.5 block rounded-lg border border-[var(--border-color)] bg-[var(--bg-surface)] p-2">
                <span className="flex items-center gap-1 font-semibold text-[0.68rem]"><ChefHat size={11} className="text-[#f97316]" /> {t("Sea Cucumber Patty")}</span>
                <span className="block text-[0.6rem] muted mt-0.5">1. {t("Grill the sea cucumber patty 2 minutes per side.")}</span>
                <span className="block text-[0.6rem] muted">2. {t("Bun, sea lettuce, pickles, tartar sauce.")}</span>
                <span className="block text-[0.6rem] muted">3. {t("Serve with Kelp Fries.")}</span>
              </span>
            </Reply>
          )}
        </div>
        <div className={small ? "mb-7" : ""}><Composer /></div>
      </div>
      <div className={`h-full flex flex-col min-h-0 ${small ? "mock-pop" : ""} ${hide("review")}`} data-testid="desktop-flow-review-panel">
        <ChatHead title={`${t("Review")} · Mr_Krabs`} />
        <div className="p-3 flex gap-1.5">
          <span className="chip chip-err !text-[0.55rem] !py-0" data-testid="desktop-flow-need-action">{t("{n} need action", { n: step >= 2 ? 2 : 1 })}</span>
          <span className="chip chip-info !text-[0.55rem] !py-0">{t("{n} ready", { n: 1 })}</span>
          <span className="chip chip-ok !text-[0.55rem] !py-0">{t("{n} approved", { n: 16 })}</span>
        </div>
        <div className="px-3">
          {step >= 2 && <Row tone="err" fresh title={`“${t(PLANKTON_Q)}”`} meta={t("From Plankton · just now")} />}
          <Row tone="err" title={`“${t("Can I give a free Kelp Shake to a regular?")}”`} meta={t("From SpongeBob · today")} />
          <Row tone="info" title={t("Coral Bits: new fryer time")} meta={t("Draft ready to approve")} />
          <Row tone="ok" title={t("Sea Cucumber Patty")} meta={t("Approved") + " · v1"} />
        </div>
      </div>
    </div>
  );
}

// Phone: Squidward checks a coupon; it's expired and SMBOI shows the current promotions.
export function PhoneFlow() {
  const step = useScript([1800, 1600, 1800, 1400]);
  const promos = [
    [t("Kelp Shake 2-for-1"), t("Every Tuesday")],
    [t("Krabby Patty Combo $5.99"), t("Until 11/30/2026")],
  ];
  return (
    <div className="h-full flex flex-col" data-testid="phone-flow">
      <ChatHead title={`${t("Chat")} · Krusty Krab`} />
      <div className="flex-1 flex flex-col gap-2 p-3 overflow-hidden">
        {step >= 1 && <Asker name="Squidward" text={t("Is the coupon KRAB10OFF still working?")} testid="phone-flow-question" />}
        {step === 1 && <Typing />}
        {step >= 2 && (
          <Reply testid="phone-flow-expired" cite={<><ShieldCheck size={9} className="text-[var(--status-ok)]" />{t("Discounts and coupons")} · v3</>}>
            <span className="font-semibold">{t("No.")}</span> {t("KRAB10OFF expired on 10/24/2026.")}
          </Reply>
        )}
        {step === 2 && <Typing />}
        {step >= 3 && (
          <Reply testid="phone-flow-promos">
            <span className="block">{t("Current promotions:")}</span>
            {promos.map(([name, when]) => (
              <span key={name} className="mt-1 flex items-center gap-1.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-surface)] px-1.5 py-1">
                <Tag size={10} className="text-[#f97316] shrink-0" />
                <span className="min-w-0"><span className="block font-semibold text-[0.62rem] leading-tight">{name}</span><span className="block text-[0.52rem] faint">{when}</span></span>
              </span>
            ))}
          </Reply>
        )}
      </div>
      <Composer />
    </div>
  );
}
